import { DialogBody } from '@blueprintjs/core'
import { useQuery } from '@tanstack/react-query'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type { BulkExportCultureMetadataResponse } from '~/api/backend/pages/bulkExportCultureMetadataAPI'
import type {
  CultureFilter,
  ObservationMetric,
} from '~/api/backend/pages/culturesPageAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Button from '~/components/buttons/Button'
import Dialog from '~/components/Dialog'
import { Skeleton } from '~/components/ui/skeleton'
import { analytics } from '~/core/analytics'
import { useFeatureFlag } from '~/core/featureFlags'
import { CulturesFilterBar } from '~/pages/cultures/CulturesFilterBar/CultureFilterBar'
import cs from './export_culture_meta_dialog.scss'

export function ExportCultureMetaDataDialog(props: {
  open: boolean
  onClose: () => void
  initialFilters: CultureFilter[]
  selectedPlateBarcodes?: string[]
}) {
  const [filters, setFilters] = useState<CultureFilter[]>(props.initialFilters)

  useEffect(() => {
    setFilters(props.initialFilters)
  }, [props.initialFilters])

  const client = useBackendClient()

  const hackathon2025 = useFeatureFlag('hackathon2025') ?? false
  const requestedMetrics = useMemo<ObservationMetric[]>(
    () => (hackathon2025 ? ['cell_concentration'] : ['confluence']),
    [hackathon2025],
  )

  // Only fetch filtered cultures if no specific plates are selected
  const { data, isLoading: isLoadingCultures } = useQuery({
    queryKey: [QUERY_KEYS.CULTURE_PAGE_LIST, filters, requestedMetrics.join(',')],
    queryFn: () =>
      client.culturesPage.getFilteredCultures(
        {
          culture_filters: filters,
        },
        requestedMetrics,
      ),
    enabled: !props.selectedPlateBarcodes || props.selectedPlateBarcodes.length === 0,
  })

  const plateWithWells =
    data?.filteredCulturePlates?.reduce((acc, plate) => {
      acc[plate.barcode] = plate.wellCultures.map(culture => culture.well)
      return acc
    }, {}) ?? {}

  const { data: bulkExportPlates, isLoading: isLoadingExportData } = useQuery({
    queryKey: [
      QUERY_KEYS.CULTURE_PAGE_BULK_EXPORT,
      plateWithWells,
      props.selectedPlateBarcodes,
      filters,
    ],
    queryFn: () => {
      // If specific plates are selected, use those barcodes
      if (props.selectedPlateBarcodes && props.selectedPlateBarcodes.length > 0) {
        return client.bulkExportCultureMetadata.getData({
          plate_barcodes: props.selectedPlateBarcodes,
          culture_filters: filters,
        })
      }
      // Otherwise use the plate_with_wells from filtered cultures
      return client.bulkExportCultureMetadata.getData({
        plate_with_wells: plateWithWells,
        culture_filters: filters,
      })
    },
    enabled:
      Object.keys(plateWithWells).length > 0 ||
      (props.selectedPlateBarcodes && props.selectedPlateBarcodes.length > 0),
  })

  // Calculate plate and well counts
  const plateCount = useMemo(() => {
    if (props.selectedPlateBarcodes && props.selectedPlateBarcodes.length > 0) {
      return props.selectedPlateBarcodes.length
    }
    return bulkExportPlates?.filteredCulturePlatesWithWells?.length ?? 0
  }, [props.selectedPlateBarcodes, bulkExportPlates])

  const wellCount = useMemo(() => {
    return (
      bulkExportPlates?.filteredCulturePlatesWithWells?.reduce(
        (acc, plate) => acc + plate.wellCultures.length,
        0,
      ) ?? 0
    )
  }, [bulkExportPlates])

  const csvData = useMemo(
    () => [
      [
        'plate_barcode',
        'well',
        'cell_line_lot',
        'cell_line',
        'passage_number',
        'status',
      ],
      ...(bulkExportPlates?.filteredCulturePlatesWithWells?.flatMap(plate => [
        ...plate.wellCultures.map(culture => [
          plate.barcode,
          culture.well,
          culture.cellLineLot ?? '',
          culture.cellLine ?? '',
          culture.passageNumber ?? '',
          culture.customCultureStatus?.name ?? '',
        ]),
      ]) ?? []),
    ],
    [bulkExportPlates],
  )

  // Combine loading states based on which mode we're in
  const isLoading =
    props.selectedPlateBarcodes && props.selectedPlateBarcodes.length > 0
      ? isLoadingExportData // Only check export data loading when using selected plates
      : isLoadingCultures || isLoadingExportData // Check both when using filters

  return (
    <Dialog
      className={cs.dialog}
      onClose={props.onClose}
      isOpen={props.open}
      noPadding={true}
    >
      <DialogBody className={cs.dialogBody}>
        <div className={cs.scrollContainer}>
          <div className='px-8 py-4 flex justify-between items-start'>
            <div className='flex flex-col gap-1'>
              <h2 className='text-lg font-medium text-gray-800 m-0'>
                Export Culture Metadata
              </h2>
              <p className='text-xs uppercase tracking-wider text-gray-500 m-0'>
                {plateCount} {plateCount === 1 ? 'PLATE' : 'PLATES'} SELECTED
              </p>
            </div>
            {isLoading ? (
              <Button
                className={cs.button}
                type='primary'
                label='Bulk Export (.csv)'
                disabled={true}
              />
            ) : (
              <CSVLink
                data={csvData}
                enclosingCharacter=''
                filename='monomer_culture_metadata.csv'
                className={cs.link}
              >
                <Button
                  className={cs.button}
                  type='primary'
                  label='Bulk Export (.csv)'
                  onClick={() => {
                    analytics.track('Completed Bulk Export Culture Metadata', {
                      numCultures: csvData.length - 1,
                      numPlates: plateCount,
                    })
                  }}
                />
              </CSVLink>
            )}
          </div>
          <div className='px-8 pb-4'>
            {isLoading ? (
              <div className='flex items-center gap-2'>
                <Skeleton className='h-4 w-48' />
              </div>
            ) : (
              <p className='text-sm text-gray-700 m-0'>
                Exporting {wellCount} cultures from {plateCount}{' '}
                {plateCount === 1 ? 'plate' : 'plates'}
              </p>
            )}
          </div>
          <div>
            <CulturesFilterBar
              className={cs.filterBar}
              filters={filters.filter(filter => filter.field === 'culture_status')}
              setFilters={setFilters}
            />
          </div>
        </div>
        <div className={cs.body}>
          {isLoading ? (
            <LoadingSkeletons />
          ) : bulkExportPlates?.filteredCulturePlatesWithWells.length === 0 ? (
            <div className={cs.noCulturesFound}>No cultures found</div>
          ) : (
            <BarcodeTable
              plates={bulkExportPlates?.filteredCulturePlatesWithWells ?? []}
            />
          )}
        </div>
      </DialogBody>
    </Dialog>
  )
}

function LoadingSkeletons() {
  return (
    <div className='flex-1 overflow-auto'>
      <table className='w-full table-fixed border-collapse'>
        <thead className='sticky top-0 bg-white z-10'>
          <tr>
            <th className='text-xs font-medium text-gray-500 px-8 py-3 text-left border-b border-gray-200 bg-white'>
              PLATE_BARCODE
            </th>
            <th className='text-xs font-medium text-gray-500 px-8 py-3 text-left border-b border-gray-200 bg-white'>
              WELL
            </th>
            <th className='text-xs font-medium text-gray-500 px-8 py-3 text-left border-b border-gray-200 bg-white'>
              CELL_LINE
            </th>
            <th className='text-xs font-medium text-gray-500 px-8 py-3 text-left border-b border-gray-200 bg-white'>
              CELL_LINE_LOT
            </th>
            <th className='text-xs font-medium text-gray-500 px-8 py-3 text-left border-b border-gray-200 bg-white'>
              PASSAGE_NUMBER
            </th>
            <th className='text-xs font-medium text-gray-500 px-8 py-3 text-left border-b border-gray-200 bg-white'>
              CULTURE_STATUS
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Generate skeleton rows */}
          {Array.from({ length: 10 }, (_, index) => (
            <tr key={index}>
              <td className='text-sm px-8 py-4 border-b border-gray-100'>
                <Skeleton className='h-4 w-24' />
              </td>
              <td className='text-sm px-8 py-4 border-b border-gray-100'>
                <Skeleton className='h-4 w-12' />
              </td>
              <td className='text-sm px-8 py-4 border-b border-gray-100'>
                <Skeleton className='h-4 w-32' />
              </td>
              <td className='text-sm px-8 py-4 border-b border-gray-100'>
                <Skeleton className='h-4 w-28' />
              </td>
              <td className='text-sm px-8 py-4 border-b border-gray-100'>
                <Skeleton className='h-4 w-16' />
              </td>
              <td className='text-sm px-8 py-4 border-b border-gray-100'>
                <Skeleton className='h-4 w-20' />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function BarcodeTable({
  plates,
}: {
  plates: BulkExportCultureMetadataResponse['filteredCulturePlatesWithWells']
}) {
  // Ref for the scrolling container
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const mappedData = useMemo(
    () =>
      plates.flatMap(plate =>
        plate.wellCultures.map(culture => ({
          barcode: plate.barcode,
          well: culture.well,
          status: culture.customCultureStatus?.name,
          cellLine: culture.cellLine ?? '',
          cellLineLot: culture.cellLineLot ?? '',
          passageNumber: culture.passageNumber ?? '',
        })),
      ),
    [plates],
  )

  const columns = useMemo(
    () => [
      {
        header: 'PLATE_BARCODE',
        accessorKey: 'barcode',
      },
      {
        header: 'WELL',
        accessorKey: 'well',
      },
      {
        header: 'CELL_LINE',
        accessorKey: 'cellLine',
      },
      {
        header: 'CELL_LINE_LOT',
        accessorKey: 'cellLineLot',
      },
      {
        header: 'PASSAGE_NUMBER',
        accessorKey: 'passageNumber',
      },
      {
        header: 'CULTURE_STATUS',
        accessorKey: 'status',
      },
    ],
    [],
  )

  const table = useReactTable({
    data: mappedData ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const { rows } = table.getRowModel()

  // Set up the virtualizer
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50, // Estimated row height in pixels
    overscan: 10, // Number of items to render outside of visible area
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()

  // Calculate padding to maintain scroll position
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0

  return (
    <div ref={tableContainerRef} className='flex-1 overflow-auto'>
      <table className='w-full table-fixed border-collapse'>
        <thead className='sticky top-0 bg-white z-10'>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  className='text-xs font-medium text-gray-500 px-8 py-3 text-left border-b border-gray-200 bg-white'
                  key={header.id}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 && (
            <tr>
              <td colSpan={columns.length} style={{ height: paddingTop }} />
            </tr>
          )}
          {virtualRows.map(virtualRow => {
            const row = rows[virtualRow.index]
            return (
              <tr key={row.id} style={{ height: virtualRow.size }}>
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className='text-sm px-8 py-4 border-b border-gray-100 break-all'
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )
          })}
          {paddingBottom > 0 && (
            <tr>
              <td colSpan={columns.length} style={{ height: paddingBottom }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
